
import { useState, useEffect } from "react";
import { GooglePlacesInput } from "./GooglePlacesInput";
import { AddressDetailsForm } from "./AddressDetailsForm";
import { useIsMobile } from "@/hooks/use-mobile";
import { useGoogleMapsKey } from "@/hooks/useGoogleMapsKey";

interface AddressSectionProps {
  title: string;
  values: {
    street: string;
    street2: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
  };
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPlaceSelect: (place: any) => void;
  prefix: "collection" | "delivery";
}

export const AddressSection = ({
  title,
  values,
  onChange,
  onPlaceSelect,
  prefix
}: AddressSectionProps) => {
  const [hasSelectedAddress, setHasSelectedAddress] = useState(false);
  const { apiKey, error, isLoading } = useGoogleMapsKey();
  const isMobile = useIsMobile();

  // Check if there's already an address in the form data
  useEffect(() => {
    if (values.street) {
      setHasSelectedAddress(true);
    }
  }, [values.street]);

  const handlePlaceSelect = (place: any) => {
    setHasSelectedAddress(true);
    onPlaceSelect(place);
  };

  const handleClearAddress = () => {
    setHasSelectedAddress(false);
    
    // Create a batch update object to clear all address fields at once
    const clearEvent = {
      target: {
        dataset: { batchUpdate: 'true' },
        batch: {
          street: "",
          street2: "",
          city: "",
          province: "",
          postalCode: "",
          country: ""
        }
      }
    } as any;
    
    // Call onChange once with batch data instead of multiple times
    onChange(clearEvent);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center p-4">Loading address search...</div>;
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  if (!apiKey) {
    return <div className="text-red-500 p-4">Unable to load address search. Please check your configuration.</div>;
  }

  return (
    <div className="address-section px-2 sm:px-0">
      <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-5">{title}</h2>
      <div>
        <AddressDetailsForm 
          values={values} 
          onChange={onChange} 
          onClear={handleClearAddress} 
          prefix={prefix} 
          onPlaceSelect={handlePlaceSelect} 
          apiKey={apiKey} 
          showAddressFields={hasSelectedAddress} 
        />
      </div>
    </div>
  );
};
